import { format, parse } from 'date-fns';
import { Injectable } from '@angular/core';
import camelcaseKeys from 'camelcase-keys';
import { PaperworkState } from '@store/reducers';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { combineLatest, Observable, of } from 'rxjs';
import { PdfService } from '@shared/helpers/pdf.helper';
import { environment } from '@environments/environment';
import { AuthService } from '@auth/services/auth.service';
import { API_URIS } from '@shared/constants/api-uris.const';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { StatusService } from '@shared/services/status.service';
import { PCV2Request } from '@shared/interfaces/request.interface';
import { Paperwork } from '@shared/interfaces/paperwork.interface';
import { FileExtension } from '@shared/constants/mime-types.const';
import { ProcedurePRM, SurgeryOrder } from '@shared/interfaces/surgery.interface';
import { buildContentPdfSurgeryKeiron } from '@clinical/surgeries/utils/build-pdf-surgery-keiron';

const BASE_API: string = environment.baseApiRME;
const BASE_API_PCV2: string = environment.baseApiPCv2;
const URI_SURGERY: string = API_URIS.clinical.surgery;
const URI_PROXY: string = API_URIS.proxy.intranet.ucc;
const URI_KEIRON_SURGERY: string = API_URIS.keiron.surgery;

interface ResponseSurgicalService {
  codfon: string;
  csu: string;
  glosa: string;
  grupo: string;
  id: string;
}

interface SurgeriesResponse {
  fonasaId: string;
  csu: string;
  glosa: string;
  grupo: string;
  id: string;
}

interface NotifyKeironDeal {
  center: string;
  id: number | null;
}

interface NotifyKeironResponse {
  deals?: NotifyKeironDeal[];
  surgery?: Paperwork<SurgeryOrder>;
}

@Injectable({
  providedIn: 'root',
})
export class SurgeryService {
  colores = ['#e9e9e9', '#f6e8fa', '#e5e6fa'];
  controlColor = 0;
  controlDate;

  constructor(private httpClient: HttpClient, private authService: AuthService, private pdfService: PdfService, private statusService: StatusService) { }

  save(body: Paperwork<SurgeryOrder>): Observable<Paperwork<SurgeryOrder>> {
    return this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_SURGERY}`, body).pipe(
      retry(3),
      map(({data}: { data: Paperwork<SurgeryOrder> }) => data),
    );
  }

  getSurgicalProcedure(txt: string) {    
    return this.httpClient.get<ResponseSurgicalService[]>(`${BASE_API}/v1/indicaciones/cirugias?text=${txt}`)
      .pipe(retry(3), tap((_) => console.log(_)), map(procedures => this.adapterSurgicalProcedure(procedures)));
  }

  adapterSurgicalProcedure(procedures: ResponseSurgicalService[]): SurgeriesResponse[] {
    return procedures.map(procedure => ({
      fonasaId: procedure.codfon,
      glosa: procedure.glosa,
      csu: procedure.csu,
      grupo: procedure.grupo,
      id: procedure.id,

    }));
  }

  findDataTable(formData) {

    const {start, end, rut} = formData;
    const body = {
      RUTMEDICO: rut,
      FINICIO: start,
      FFIN: end
    };

    const dateFrom = format(parse(start, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');
    const dateTo = format(parse(end, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd');

    // 'https://api.ucchristus.io/analitica/v1/agendas/pabellones/profesionales/17621991-2?dateFrom=2025-05-28&dateTo=2025-05-29'
    const suffixURLv1 = 'LISTA/AGENDAMIENTOS/PABELLON';
    const apiName = '&apiName=filemaker';
    const environ = `&environment=${environment.stage}`;

    const request1 = this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_PROXY}${suffixURLv1}${apiName}${environ}`, body);
    const request2 = this.httpClient.get<PCV2Request>(`${BASE_API_PCV2}/analitica/v1/agendas/pabellones/profesionales/${rut}?dateFrom=${dateFrom}&dateTo=${dateTo}`);

    return combineLatest([request1, request2]).pipe(
      map(([response1, response2]) => {
        const data1 = this.transformData(response1.data);

        const data2 = this.transformData(response2.data);
        return [...data1, ...data2];
      }),
      catchError((error) => of(error))
    );
  }

  transformData(data: any) {
    const {listAgendamientos} = camelcaseKeys(data, {deep: true});
    const {datos} = listAgendamientos;


    const newPavilions = datos.map((dato: any, index: number) => {
      const {codPabellon, descPabellon, runPaciente, nombrePaciente} = dato;
      const {diagnostico, horainicio, horatermino, fecha} = dato;

      const {procedimiento, descUnidad, descCentro, estado, fechaAnulado} = dato;

      const pabellon = this.setPabellon(codPabellon, descPabellon);
      const date = format(new Date(fecha), 'dd/MM/yyyy');
      if (horainicio && horatermino) {
        const hour = `${this.formatHour(horainicio)} a ${this.formatHour(horatermino)}`;
        const color = this.setColor(fecha);


        return {
          hour,
          color,
          pabellon,
          sort: date,
          descCentro,
          descUnidad,
          runPaciente,
          diagnostico,
          procedimiento,
          nombrePaciente,
          createdAt: date,
          estado: `${estado} ${fechaAnulado || ''}`
        };
      }
      // TODO: hice esto un lunes que tenia vomitos y dorlor de estomago, si alguien lo ve antes que yo verificar si se puede mejorar el codigo, el funcionamiento esta OK
      return {
        hour: horainicio,
        color: "#e9e9e9",
        pabellon,
        sort: date,
        descCentro,
        descUnidad,
        runPaciente,
        diagnostico,
        procedimiento,
        nombrePaciente,
        createdAt: date,
        estado: `${estado} ${fechaAnulado || ''}`
      };
    });

    return newPavilions;
  }

  setColor(fecha) {
    if (!this.controlDate) this.controlDate = fecha;
    if (this.controlDate !== fecha && this.controlDate) {
      this.controlDate = fecha;
      this.controlColor = this.controlColor + 1;
    }
    if (this.controlColor === 3) this.controlColor = 0;
    return this.colores[this.controlColor];
  }

  setPabellon(pabellon: string, descPabellon: string | null | undefined) {
    const desc = descPabellon ? descPabellon.toUpperCase() : '';
    return `${pabellon ?? ''} ${desc}`;
  }

  formatHour(hour: string) {
    return hour.split(':').slice(0, 2).join(':');
  }

  notifyKeiron(formData: FormData) {
    const endpoint = `${BASE_API_PCV2}/${URI_KEIRON_SURGERY}`;
    return this.httpClient.post<PCV2Request>(`${endpoint}`, formData).pipe(
      // retry(3),
      map(({data}) => this.validateNotifyKeironResponse(data as NotifyKeironResponse, formData)),
    );
  }

  notifyAllKeiron(formsData: FormData[]) {
    const request = formsData.map(formData => this.notifyKeiron(formData));
    return combineLatest(request);
  }

  private validateNotifyKeironResponse(response: NotifyKeironResponse, formData: FormData) {
    const center = formData.get('center');
    const hasValidDeal = Array.isArray(response?.deals) && response.deals.some((deal) => deal?.id !== null && deal?.id !== undefined);

    if (hasValidDeal) return {...response, center};

    const errorMessage = `La notificacion a Keiron no devolvio un identificador valido para el centro ${center}.`;
    throw new HttpErrorResponse({
      error: {message: errorMessage, response},
      status: 502,
      statusText: 'Keiron notification invalid response',
      url: `${BASE_API_PCV2}/${URI_KEIRON_SURGERY}`,
    });
  }

  /**
   * Construye formularios para enviar a keiron. Retorna un formulario por cada centro en la orden
   */
  async buildNotifyKeironFormsData(paperworkState): Promise<FormData> {
    const {patient, professional, shortId, content} = paperworkState.paperwork;
    const file = await this.getKeironPDFs(paperworkState);
    const [surgeryOrder] = content;
    const notifyFormData = new FormData();

    notifyFormData.append('file', file.file);
    notifyFormData.append('center', surgeryOrder.hospitalizations?.centers.map((center) => center.center).join(','));
    notifyFormData.append('id', shortId);
    notifyFormData.append('email', patient.email);
    notifyFormData.append('firstName', patient.names);
    notifyFormData.append('lastName', patient.surnames);
    notifyFormData.append('phoneNumber', patient.phone);
    notifyFormData.append('doctorRut', professional.rut);
    notifyFormData.append('description', surgeryOrder.observations?.budget);
    notifyFormData.append('stayType', surgeryOrder.hospitalizations?.stayType);
    notifyFormData.append('priority', surgeryOrder.priority);
    notifyFormData.append('hasAllergy', surgeryOrder.hasAllergy);
    notifyFormData.append('diagnosis', surgeryOrder.diagnosis);
    notifyFormData.append('donorBlood', surgeryOrder.donorBlood);
    notifyFormData.append('allergy', surgeryOrder.allergy);
    notifyFormData.append('correlativeCrm', shortId);
    notifyFormData.append('procedures', surgeryOrder.procedures.map((procedure): ProcedurePRM => procedure.glosa.trim()).join(','));
    notifyFormData.append('proceduresCode', surgeryOrder.procedures.map((procedure): ProcedurePRM => procedure.csu.trim()).join(','));
    notifyFormData.append('searchType', patient.documentType === 'NationalId' ? 'patientRut' : 'passport');
    notifyFormData.append('patientRut', patient.documentType === 'NationalId' ? patient.documentNumber : '');
    notifyFormData.append('passport', patient.documentType === 'NationalId' ? '' : patient.documentNumber);
    notifyFormData.append('birthDate', format(new Date(patient.birth), 'yyyy/MM/dd'));
    notifyFormData.append('gender', patient.gender === 'Female' ? 'F' : 'M');
    notifyFormData.append('address', `${patient.address.street} ${patient.address.streetNumber} ${patient.address.apartmentNumber}`);
    notifyFormData.append('city', patient.address.locality);
    notifyFormData.append('doctorName', `${professional.names} ${professional.surnames}`);
    notifyFormData.append('roboticOrigin', surgeryOrder.roboticSurgery);
    if (paperworkState.paperwork?.appointment) {
      notifyFormData.append('appointmentDate', format(new Date(paperworkState.paperwork?.appointment?.fromDatetime), 'yyyy/MM/dd'));
      notifyFormData.append('appointmentId', paperworkState.paperwork?.appointment?.id);
    }
    notifyFormData.append('pk', paperworkState.paperwork?.pk || '');
    notifyFormData.append('sk', paperworkState.paperwork?.sk || '');
    
    notifyFormData.append('specialOrder', surgeryOrder.agreements === 'Otro' ? surgeryOrder.other || '' : surgeryOrder.agreements);

    return notifyFormData;
  }

  async getKeironPDFs(paperworkState: PaperworkState) {
    let docBase64 = null;
    const {definition} = paperworkState;
    const groups = this.groupedRows(paperworkState);
    const docs = {pdfs: [], paperwork: {} as Paperwork};
    const {paperwork} = paperworkState;
    const professional = this.authService.professionalRef;

    for await (const group of groups) {
      const {title} = group;
      const {config, docTitle} = definition.submodule;
      const body = buildContentPdfSurgeryKeiron(paperwork, group);
      const pdfParams = {
        config: {...config, isPreview: false},
        paperwork: {...paperwork, professional},
        group, title, body, docTitle,
      };
      const base64 = await this.pdfService.createPdf(pdfParams);
      const pdf = {base64, title: title + ' Keiron', isAttached: true};

      docBase64 = base64;
      docs.pdfs = [pdf, ...docs.pdfs];
    }

    const extension: FileExtension = 'pdf';
    const documents = docs.pdfs.map((pdf) => ({
      ...pdf,
      extension,
      path: definition.module,
      file: this.pdfService.base64ToBlob(pdf.base64),
    }));
    return documents[0];
  }

  groupedRows({paperwork}): any[] {
    const groups: string[] = [...new Set<string>(paperwork.content.map((form) => form?.group))];
    return groups.map<any>((group) => ({
      title: group ? group.toLowerCase() : '',
      rows: paperwork.content.filter((form) => form.group === group),
    }));
  }

  public getSurgeyMessagePopup() {
    return `<p>Estimado Profesional:</p>
            <p>Su solicitud fue enviada y recepcionada con éxito por el equipo de Acceso Dedicado, en un plazo de máximo 72 hrs.
            será entregado el presupuesto al paciente.
            Si requerimos alguna aclaración adicional la enfermera del equipo tomará contacto con ud.</p>
            <p>Le recordamos nuestros canales de atención:</p>
            <p><b>Atención Presencial:</b></p>
            <p>Hospital Clínico UC CHRISTUS, Primer piso. Lunes a jueves, de 8:30 a 17:00 hrs; viernes de 8:30 a 16:30 hrs.</p>
            <p>Centro Médico Santa Lucía, Primer piso. Lunes a jueves, de 9:00 a 17:00 hrs.; viernes de 8:30 a 16:30 hrs.</p>
            <p>Clínica San Carlos de Apoquindo, Edificio Centro Médico, Piso -1. Lunes a jueves, de 9:00 a 17:00 hrs.;
            viernes de 8:30 a 16:30 hrs.</p>
            <p><b>Correo:</b> presupuestos.hospitalarios@ucchristus.cl</p>`;
  }

  /**
   * Obtiene una lista actualizada de procedimientos quirúrgicos a partir de un objeto de órdenes de cirugía.
   */
  getUpdatedSurgeries(surgeries: SurgeryOrder): Observable<ProcedurePRM[]> {
    const { procedures } = surgeries;
    const updatedSurgeriesRequests = procedures.map(({ csu }) => this.getSurgeryByGroupAndId(csu));
    return combineLatest(updatedSurgeriesRequests).pipe(
      map((updatedSurgeries) => {
        return procedures
          .map((sur, i) => updatedSurgeries[i] ? {
              ...sur, ...updatedSurgeries[i],
              observations: sur.observations,
          } : null)
          .filter(sur => sur !== null)
      }),
      catchError(() => of(procedures)),
    );
  };

  /**
   * Obtiene una cirugía específica por grupo e identificador (CSU).
   *
   * Realiza una solicitud HTTP para recuperar una lista de cirugías que coincidan con el texto proporcionado (CSU),
   * luego convierte la respuesta al formato esperado y retorna el primer resultado si la operación es exitosa.
   * En caso de error, maneja el error utilizando el servicio de estado.
   *
   * @param csu - El identificador o texto del grupo de cirugía a buscar.
   * @returns Un observable que emite un objeto `ProcedurePRM` correspondiente a la cirugía encontrada, o `null` si no se encuentra.
   */
  getSurgeryByGroupAndId(csu: string): Observable<ProcedurePRM> {
    const ENDPOINT = `v1/indicaciones/cirugias?text=${csu}`;
    const url = `${BASE_API}/${ENDPOINT}`;
    return this.httpClient.get<ProcedurePRM[]>(url)
      .pipe(
        retry(3),
        map((surgeries) => {
          const result = this.convertSurgeriesToPCv2Surgery(surgeries);
          return result?.status === 'ok' ? result.request[0] : null;
        }),
        catchError(this.statusService.catchError),
      );
  }

  /**
   * Convierte una lista de cirugías en el formato requerido por PCv2.
   *
   * @param surgery - Arreglo de objetos de cirugía a convertir.
   * @returns Un objeto con el estado de la conversión ('ok' o 'void') y la lista de cirugías convertidas.
   *
   * Si el arreglo de entrada está vacío, retorna un objeto con estado 'void' y una lista vacía.
   * Cada cirugía convertida incluye los campos: glosa, csu, fonasaId (como string si existe), y observations.
   */
  convertSurgeriesToPCv2Surgery(surgery): { status: string; request: any[] } {
    if (surgery.length === 0) return {status: 'void', request: []};
    const surgeries = surgery.map(su => {
        const { csu, codfon, glosa, obs } = su;
        return {
            glosa,
            csu,
            fonasaId: codfon ? codfon.toString() : undefined, 
            observations: obs,
        };
    });
    return {status: 'ok', request: surgeries};
  }
}
